/**
 * CustomerInvoices — the 360 Invoices tab. A unified billing list: Pegasus-native
 * invoices (the office builder's output — service, standalone "New invoice", and
 * completed-dispatch invoices) MERGED with the customer's ESC history from
 * `mirror_sales`, newest first. A just-created invoice shows up here immediately.
 *
 * Competitive frame: ServiceTitan / Jobber / FieldEdge give one billing list with
 * #, date, type, total — AND a "New invoice" button right on the customer page
 * (Jobber/Housecall/QuickBooks: bill a customer with no job behind it). We match the
 * list + the standalone "New invoice" (Seth 2026-07-01). Pegasus rows carry a
 * Draft/Issued pill; ESC rows are the historical remainder. Paid/open balance still
 * waits on the AR read path (not mirrored yet — honest rather than a fake "Paid").
 *
 * Chimera-aware (BUILD-17): when this reused ESC account has a dated ownership
 * handoff, invoices before it belong to the PRIOR owner — a divider marks the line
 * rather than implying the current owner ran up the whole tab.
 */
import { Fragment, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Receipt, ChevronRight, Plus, Trash2, HandCoins, Printer, Building2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { format, parseISO } from "date-fns";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { InvoiceBuilder } from "@/features/invoice/InvoiceBuilder";
import { useCustomerInvoices } from "@/features/invoice-detail/api";
import { useCustomerBalance, type LocationRow } from "./api";
import { usePegasusCustomerInvoices, useDeleteInvoice } from "@/features/invoice/officeInvoice";
import { useInvoiceSendStatuses, InvoiceSentChip } from "@/features/comms/invoiceSend";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOwnerEra } from "./ownerEra";
import { AllPropertiesChip } from "./ScopeChip";
// PAY-02: the payment ledger — Collect payment + Paid/Partial derived badges + refunds.
// 2026-08-07 (Seth): payments live ON their invoices — each Pegasus invoice row
// grows sub-rows for the payments applied to it; OtherPaymentsCard catches the rest.
import { CollectPaymentModal, type OpenInvoice } from "@/features/payments/CollectPaymentModal";
import { InvoicePaymentRows, OtherPaymentsCard, RefundModal } from "@/features/payments/PaymentsList";
import { useCustomerPayments, invoicePaidMap, escInvoicePaidMap, usePaymentTerms, type PegasusPayment } from "@/features/payments/payments";
// PAY-02c: refund documentation chips (files linked to a refund via pegasus_file.refund_id)
import { useCustomerFiles, useSignedUrls, type PegasusFile } from "@/features/files/filesCore";
import { InfoTip } from "@/components/InfoTip";

const money = (n: number | null) =>
  n == null
    ? "—"
    : n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });

interface URow {
  key: string;
  kind: "pegasus" | "esc";
  date: string | null; // date-only ISO (YYYY-MM-DD)
  number: string;
  label: string;
  amount: number | null;
  to: string;
  status?: string | null;
  locno: string | null;
}

// ---------------------------------------------------------------------------
// AR-360 (Seth 2026-08-21, the Verde question: "how do we find which invoices
// have a balance… which addresses… and apply payment?"). The ESC AR subledger
// (mirror_receivable) knows, invoice by invoice, what's open and at WHICH
// property — this hook reads it so every ESC row can wear an honest
// Paid / "$X open" pill instead of the old "balances live in ESC" shrug.
// ---------------------------------------------------------------------------
interface EscAr { open: number; locno: string | null; paidOff: boolean }

function useEscReceivables(custno: string) {
  return useQuery({
    queryKey: ["esc_receivables", custno],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, EscAr>> => {
      const { data, error } = await supabase
        .from("mirror_receivable")
        .select("invoice, locno, inv_amt, paid, paid_off")
        .eq("custno", custno)
        .limit(2000);
      if (error) throw error;
      const m = new Map<string, EscAr>();
      for (const r of (data ?? []) as { invoice: string | number; locno: string | null; inv_amt: number | null; paid: number | null; paid_off: string | null }[]) {
        const owed = Math.max(0, (r.inv_amt ?? 0) - (r.paid ?? 0));
        m.set(String(r.invoice), { open: r.paid_off ? 0 : owed, locno: r.locno ?? null, paidOff: !!r.paid_off || owed <= 0.005 });
      }
      return m;
    },
  });
}

function statusPill(status: string | null | undefined) {
  if (status === "draft")
    return (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
        Draft
      </span>
    );
  if (status === "issued")
    return (
      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
        Issued
      </span>
    );
  // FIELD-PAY-01: a deposit invoice is voided when its job closes — its payment
  // moved onto the final invoice, so it's never owed on its own.
  if (status === "void")
    return (
      <span title="Nothing owed on this one — a deposit's payment moves to the final invoice when the job closes"
        className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        Void
      </span>
    );
  return null;
}

export function CustomerInvoices({
  custno,
  locno,
  customerName,
  multiProperty,
  locations,
}: {
  custno: string;
  locno?: string | null;
  customerName?: string | null;
  /** 360-AUDIT wave 2 (B): invoices are whole-account (AR follows the customer) — label it. */
  multiProperty?: boolean;
  /** AR-360: address labels for the per-property grouping + statement. */
  locations?: LocationRow[];
}) {
  const { data: escData, isLoading: escLoading } = useCustomerInvoices(custno);
  const { data: pegData, isLoading: pegLoading } = usePegasusCustomerInvoices(custno);
  const navigate = useNavigate();
  // INVOICE-SENT-01: did this invoice reach the customer? Read from the outbox.
  const sends = useInvoiceSendStatuses((pegData ?? []).map((p) => p.id)).data;
  const era = useOwnerEra(custno, locno);

  // INV-DELETE: permission-gated delete of a Pegasus invoice (confirm modal).
  const canDelete = useCan("invoices.delete");
  const del = useDeleteInvoice();
  const [confirmDel, setConfirmDel] = useState<URow | null>(null);

  // PAY-02: derived paid state per invoice (ledger allocations minus refunds — never a flag)
  const canCollect = useCan("payments.collect");
  const canRefund = useCan("payments.refund");
  const { data: payments } = useCustomerPayments(custno);
  const paidMap = invoicePaidMap(payments);
  // AR-360: ESC-side open state (mirror_receivable) + payments recorded HERE
  // against ESC invoice numbers — both feed the per-invoice pills.
  const { data: escAr } = useEscReceivables(custno);
  const escPaid = escInvoicePaidMap(payments);
  const { data: bal } = useCustomerBalance(custno);
  const locLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of locations ?? []) m.set(l.locno, l.add1 ?? l.locname ?? `Property ${l.locno}`);
    return m;
  }, [locations]);
  /** Net ESC open = mirror open minus what's already recorded on the Pegasus ledger. */
  const escNetOpen = (invNo: string): { known: boolean; net: number; recordedHere: number; grossOpen: number } => {
    const ar = escAr?.get(invNo);
    if (!ar) return { known: false, net: 0, recordedHere: 0, grossOpen: 0 };
    const rec = escPaid.get(invNo) ?? 0;
    return { known: true, net: Math.max(0, ar.open - rec), recordedHere: Math.min(rec, ar.open), grossOpen: ar.open };
  };
  // Open / Paid filter + the multi-select that answers "we're paying these two".
  const [statusFilter, setStatusFilter] = useState<"all" | "open" | "paid">("all");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const toggleSel = (key: string) =>
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const [collecting, setCollecting] = useState<{ initial: string | null; invoices?: OpenInvoice[]; preselectAll?: boolean } | null>(null);
  const [refunding, setRefunding] = useState<PegasusPayment | null>(null);
  // PAY-02c: refund documentation — files linked to any of this customer's refunds
  const { data: custFiles } = useCustomerFiles(custno);
  const refundDocs = (custFiles ?? []).filter((f) => f.refund_id);
  const refundFiles = new Map<string, PegasusFile[]>();
  for (const f of refundDocs) {
    const list = refundFiles.get(f.refund_id!) ?? [];
    list.push(f);
    refundFiles.set(f.refund_id!, list);
  }
  const { data: fileUrls } = useSignedUrls(refundDocs);
  const knownInvoiceIds = new Set((pegData ?? []).map((p) => p.id));
  const openInvoices: OpenInvoice[] = (pegData ?? [])
    .filter((p) => p.status !== "draft" && p.status !== "void" && p.total != null)
    .map((p) => ({
      id: p.id,
      number: p.number,
      date: (p.date ?? "").slice(0, 10) || null,
      label: p.type ? p.type.charAt(0).toUpperCase() + p.type.slice(1) : "Invoice",
      total: Number(p.total),
      balance: Math.max(0, Number(p.total) - (paidMap.get(p.id) ?? 0)),
      property: p.locno ? (locLabel.get(p.locno) ?? null) : null,
    }))
    .filter((i) => i.balance > 0.005);
  // AR-360: the ESC-side open invoices join the collectable list — the edge fn
  // + allocation table record them by esc_invoice_no (server support shipped
  // at PAY-02; this is the first UI that uses it).
  const escOpenInvoices: OpenInvoice[] = [...(escAr?.entries() ?? [])]
    .map(([invNo, ar]) => {
      const info = escNetOpen(invNo);
      const src = (escData ?? []).find((r) => String(r.invoice) === invNo);
      return {
        id: `esc-${invNo}`,
        escNo: invNo,
        number: invNo,
        date: (src?.inv_date ?? "").slice(0, 10) || null,
        label: [src?.inv_type, src?.dept].filter(Boolean).join(" · ") || "ESC invoice",
        total: ar.open,
        balance: info.net,
        property: ar.locno ? (locLabel.get(ar.locno) ?? null) : null,
      };
    })
    .filter((i) => i.balance > 0.005);
  const allOpenInvoices: OpenInvoice[] = [...openInvoices, ...escOpenInvoices];
  const collectBtn = canCollect ? (
    <Button size="sm" onClick={() => setCollecting({ initial: null })} disabled={allOpenInvoices.length === 0}
      title={allOpenInvoices.length === 0 ? "No open invoices with a balance" : undefined}>
      <HandCoins className="h-3.5 w-3.5" /> Collect Payment
    </Button>
  ) : null;
  // PAY-04: payment terms — an unpaid issued invoice older than the terms
  // window reads Past due (amber). Terms are a Settings dial (payment_terms_days).
  const { data: termsDays } = usePaymentTerms();
  const paidPill = (r: URow) => {
    if (r.kind !== "pegasus" || r.amount == null || r.status === "draft" || r.status === "void") return null;
    const id = r.key.replace(/^peg-/, "");
    const paid = paidMap.get(id) ?? 0;
    if (paid >= r.amount - 0.005) {
      return <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Paid</span>;
    }
    const overdue = (r.status === "issued" || r.status === "finalized") && !!r.date && !!termsDays &&
      Date.parse(r.date) < Date.now() - termsDays * 86_400_000;
    if (overdue) {
      return (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
          Past due{paid > 0.005 ? ` · ${money(r.amount - paid)} open` : ""}
        </span>
      );
    }
    if (paid > 0.005) {
      return <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-brand-700">{money(r.amount - paid)} open</span>;
    }
    return null;
  };
  // AR-360: the ESC twin — open/paid from the AR mirror, minus anything the
  // office already recorded on the Pegasus ledger against that invoice number.
  const escPill = (r: URow) => {
    if (r.kind !== "esc") return null;
    const info = escNetOpen(r.number);
    if (!info.known) return null; // not in the AR subledger — say nothing rather than fake "Paid"
    if (info.net <= 0.005) {
      if (info.recordedHere > 0.005 && info.grossOpen > 0.005) {
        return (
          <span
            title="Recorded on the Pegasus ledger — post it in ESC too until cutover (the AR mirror refreshes overnight)."
            className="cursor-help rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700"
          >
            Paid · recorded here
          </span>
        );
      }
      return <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Paid</span>;
    }
    return (
      <span
        title={info.recordedHere > 0.005 ? `${money(info.recordedHere)} already recorded in Pegasus — the rest is still owed.` : "Open per the ESC AR mirror (refreshes overnight)."}
        className="cursor-help rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-amber-800"
      >
        {money(info.net)} open
      </span>
    );
  };
  const rowOpen = (r: URow): boolean =>
    r.kind === "pegasus"
      ? openInvoices.some((o) => `peg-${o.id}` === r.key)
      : escNetOpen(r.number).net > 0.005;
  const rowPaid = (r: URow): boolean => {
    if (r.kind === "pegasus") {
      if (r.amount == null || r.status === "draft" || r.status === "void") return false;
      return (paidMap.get(r.key.replace(/^peg-/, "")) ?? 0) >= r.amount - 0.005;
    }
    const info = escNetOpen(r.number);
    return info.known && info.net <= 0.005;
  };

  // Standalone "New invoice" — bill this customer with no dispatch behind it.
  const [creating, setCreating] = useState(false);
  const builder = creating ? (
    <InvoiceBuilder
      customer={{ custno, locno: locno ?? null, name: customerName ?? null }}
      onClose={() => setCreating(false)}
    />
  ) : null;
  const newInvoiceBtn = (
    <Button variant="secondary" size="sm" onClick={() => setCreating(true)}>
      <Plus className="h-3.5 w-3.5" /> New invoice
    </Button>
  );

  // Unify Pegasus + ESC into one date-sorted list (newest first) — a fresh Pegasus
  // invoice sorts to the top.
  const pegRows: URow[] = (pegData ?? []).map((p) => ({
    key: `peg-${p.id}`,
    kind: "pegasus",
    date: (p.date ?? "").slice(0, 10) || null,
    number: p.number,
    label: (p.type ? p.type.charAt(0).toUpperCase() + p.type.slice(1) : "Invoice"),
    amount: p.total,
    to: `/invoices/${p.id}`,
    status: p.status,
    locno: p.locno ?? null,
  }));
  const escRows: URow[] = (escData ?? []).map((r) => ({
    key: `esc-${r.invoice}`,
    kind: "esc",
    date: (r.inv_date ?? "").slice(0, 10) || null,
    number: String(r.invoice),
    label: [r.inv_type, r.dept].filter(Boolean).join(" · ") || "—",
    amount: r.inv_amount ?? null,
    to: `/invoice/${r.invoice}`,
    locno: r.locno ?? escAr?.get(String(r.invoice))?.locno ?? null,
  }));
  const unified = [...pegRows, ...escRows].sort(
    (a, b) => (b.date ? Date.parse(b.date) : 0) - (a.date ? Date.parse(a.date) : 0),
  );

  if (escLoading || pegLoading) {
    return <Card className="p-6 text-sm text-slate-400">Loading invoices…</Card>;
  }

  if (unified.length === 0) {
    return (
      <>
        <Card className="p-10 text-center">
          <Receipt className="mx-auto mb-2 h-5 w-5 text-slate-300" />
          <p className="text-sm text-slate-500">No invoices on file for this customer.</p>
          <div className="mt-4 flex justify-center">{newInvoiceBtn}</div>
        </Card>
        {/* Even with no invoices, any recorded payments must stay visible. */}
        <OtherPaymentsCard payments={payments ?? []} knownInvoiceIds={knownInvoiceIds}
          canRefund={canRefund} onRefund={setRefunding} />
        {refunding && (
          <RefundModal target={refunding} custno={custno} onClose={() => setRefunding(null)} />
        )}
        {builder}
      </>
    );
  }

  // A void invoice (a deposit applied to its job's final invoice) isn't billed twice.
  const total = unified.reduce((s, r) => s + (r.status === "void" ? 0 : (r.amount ?? 0)), 0);
  // AR-360: the Open / Paid lenses. Open on a multi-property account groups by
  // property (the Verde read: which address owes what).
  const filtered =
    statusFilter === "open" ? unified.filter(rowOpen)
    : statusFilter === "paid" ? unified.filter(rowPaid)
    : unified;
  const openAmt = (r: URow): number =>
    r.kind === "pegasus"
      ? (openInvoices.find((o) => `peg-${o.id}` === r.key)?.balance ?? 0)
      : escNetOpen(r.number).net;
  type Item = { header: { label: string; subtotal: number; count: number } } | { row: URow };
  const items: Item[] = (() => {
    if (statusFilter !== "open" || !multiProperty) return filtered.map((row) => ({ row }));
    const byLoc = new Map<string, URow[]>();
    for (const r of filtered) {
      const k = r.locno ?? "?";
      byLoc.set(k, [...(byLoc.get(k) ?? []), r]);
    }
    const out: Item[] = [];
    for (const [k, rows] of [...byLoc.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
      out.push({ header: { label: k === "?" ? "Property not recorded" : (locLabel.get(k) ?? `Property ${k}`), subtotal: rows.reduce((sum, r) => sum + openAmt(r), 0), count: rows.length } });
      for (const r of rows) out.push({ row: r });
    }
    return out;
  })();
  const selRows = unified.filter((r) => sel.has(r.key) && rowOpen(r));
  const selTotal = selRows.reduce((sum, r) => sum + openAmt(r), 0);
  const collectSelected = () => {
    const invs = selRows
      .map((r): OpenInvoice | null =>
        r.kind === "pegasus"
          ? (openInvoices.find((o) => `peg-${o.id}` === r.key) ?? null)
          : (allOpenInvoices.find((o) => o.id === r.key) ?? null))
      .filter(Boolean) as OpenInvoice[];
    if (invs.length > 0) setCollecting({ initial: null, invoices: invs, preselectAll: true });
  };
  const handoff = era.handoffDate ? parseISO(era.handoffDate) : null;
  let dividerShown = false;

  return (
    <>
      <Card className="overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-900">Invoices</h3>
            <InfoTip label="About invoices">
              Pegasus invoices (Draft / Issued) sit alongside the ESC history (read-only). Open / Paid on ESC rows comes from the
              AR mirror (refreshes overnight); payments recorded here show on both kinds immediately — post ESC-invoice payments
              in ESC too until cutover.
            </InfoTip>
            <AllPropertiesChip show={!!multiProperty} />
            <span className="text-xs text-slate-400">
              {unified.length} · {money(total)} total
            </span>
            {/* AR-360: the Open lens is the collections view — count + open $. */}
            <span className="ml-1 inline-flex items-center overflow-hidden rounded-lg border border-hairline bg-white">
              {([
                ["all", "All"],
                ["open", `Open${allOpenInvoices.length ? ` ${allOpenInvoices.length}` : ""}`],
                ["paid", "Paid"],
              ] as const).map(([k, label], i) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setStatusFilter(k)}
                  className={
                    (i > 0 ? "border-l border-hairline " : "") +
                    "px-2.5 py-1 text-[11px] font-semibold transition-colors " +
                    (statusFilter === k ? "bg-brand-700 text-white" : "text-slate-500 hover:bg-slate-50")
                  }
                >
                  {label}
                </button>
              ))}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {allOpenInvoices.length > 0 && (
              <Button variant="secondary" size="sm"
                onClick={() => openStatementWindow({ customerName: customerName ?? custno, custno, invoices: allOpenInvoices, bal })}
                title="Print / save a statement of every open invoice, grouped by property">
                <Printer className="h-3.5 w-3.5" /> Statement
              </Button>
            )}
            {collectBtn}
            {newInvoiceBtn}
          </div>
        </div>

        <ul className="divide-y divide-hairline">
          {items.map((item, idx) => {
            if ("header" in item) {
              return (
                <li key={`grp-${idx}`} className="flex items-center gap-2 bg-slate-50/80 px-4 py-1.5 text-[11px] font-semibold text-slate-600">
                  <Building2 className="h-3 w-3 text-slate-400" />
                  <span className="truncate">{item.header.label}</span>
                  <span className="ml-auto shrink-0 tabular-nums text-amber-700">{money(item.header.subtotal)} open · {item.header.count}</span>
                </li>
              );
            }
            const r = item.row;
            const d = r.date ? parseISO(r.date) : null;
            let divider = null;
            if (statusFilter === "all" && handoff && era.hasTransfer && !dividerShown && d && d < handoff) {
              dividerShown = true;
              divider = (
                <li className="flex items-center gap-1.5 bg-slate-50/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Before {format(handoff, "MMM yyyy")} · {era.priorOwnerName}&apos;s era (prior owner)
                </li>
              );
            }
            return (
              <Fragment key={r.key}>
                {divider}
                <li className="group flex items-center">
                  {/* AR-360: pick the invoices the caller is paying — the bar
                      below totals them and hands the set to Collect Payment. */}
                  {canCollect && rowOpen(r) && (
                    <input
                      type="checkbox"
                      className="ml-3 h-4 w-4 shrink-0 accent-brand-600"
                      checked={sel.has(r.key)}
                      onChange={() => toggleSel(r.key)}
                      title="Select for payment"
                    />
                  )}
                  <Link
                    to={r.to}
                    className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50"
                  >
                    <span className="w-24 shrink-0 text-xs tabular-nums text-slate-500">
                      {d ? format(d, "MMM d, yyyy") : "—"}
                    </span>
                    <span className="w-20 shrink-0 truncate font-mono text-xs text-slate-700">
                      #{r.number}
                    </span>
                    <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-xs text-slate-500">
                      <span className="truncate">{r.label}</span>
                      {multiProperty && statusFilter !== "open" && r.locno && (
                        <span className="hidden max-w-[160px] shrink-0 truncate rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 sm:inline" title={locLabel.get(r.locno) ?? r.locno}>
                          {locLabel.get(r.locno) ?? r.locno}
                        </span>
                      )}
                      {statusPill(r.status)}
                      {r.kind === "esc" ? escPill(r) : paidPill(r)}
                      {r.kind === "pegasus" && r.status === "issued" && (
                        <InvoiceSentChip
                          size="xs"
                          status={sends?.get(r.key.replace(/^peg-/, ""))}
                          onSend={() => navigate(`/invoices/${r.key.replace(/^peg-/, "")}?email=1`)}
                        />
                      )}
                    </span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-800">
                      {money(r.amount)}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                  </Link>
                  {/* Row-level Collect (hover) — an open Pegasus invoice takes a payment
                      right here, preselected to this invoice. */}
                  {canCollect && rowOpen(r) && (
                    <button
                      type="button"
                      onClick={() => setCollecting({ initial: r.kind === "pegasus" ? r.key.replace(/^peg-/, "") : r.key })}
                      title="Collect a payment on this invoice"
                      className="shrink-0 rounded-md p-1.5 text-slate-300 opacity-40 transition-all hover:bg-emerald-50 hover:text-emerald-600 focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      <HandCoins className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {/* Delete lives OUTSIDE the Link so it can't navigate; Pegasus
                      rows only (ESC history is read-only), permission-gated. */}
                  {r.kind === "pegasus" && canDelete && (
                    <button
                      type="button"
                      onClick={() => setConfirmDel(r)}
                      title="Delete this Pegasus invoice"
                      className="mr-2 shrink-0 rounded-md p-1.5 text-slate-300 opacity-40 transition-all hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
                {/* The invoice's payments, right under it — what paid this, with what,
                    when, plus fee/refund detail. Zero clicks to reconcile. */}
                {r.kind === "pegasus" && (
                  <InvoicePaymentRows
                    invoiceId={r.key.replace(/^peg-/, "")}
                    payments={payments ?? []}
                    canRefund={canRefund}
                    onRefund={setRefunding}
                    refundFiles={refundFiles}
                    fileUrls={fileUrls}
                  />
                )}
              </Fragment>
            );
          })}
        </ul>

        {sel.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-t border-brand-200 bg-brand-50/60 px-4 py-2.5">
            <span className="text-sm font-medium text-slate-700">
              {selRows.length} invoice{selRows.length === 1 ? "" : "s"} selected ·{" "}
              <b className="tabular-nums text-slate-900">{money(selTotal)}</b>
            </span>
            <span className="ml-auto flex items-center gap-2">
              <button type="button" onClick={() => setSel(new Set())}
                className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-700">
                Clear
              </button>
              <Button size="sm" onClick={collectSelected} disabled={selRows.length === 0}>
                <HandCoins className="h-3.5 w-3.5" /> Collect {money(selTotal)}
              </Button>
            </span>
          </div>
        )}
      </Card>

      {/* Payments live UNDER their invoices (sub-rows above); this card only catches
          payments with no invoice row to sit under, so money never disappears. */}
      <OtherPaymentsCard payments={payments ?? []} knownInvoiceIds={knownInvoiceIds}
        canRefund={canRefund} onRefund={setRefunding} />

      {collecting && (
        <CollectPaymentModal
          custno={custno}
          customerName={customerName}
          invoices={collecting.invoices ?? allOpenInvoices}
          initialInvoiceId={collecting.initial}
          preselectAll={collecting.preselectAll}
          onClose={() => { setCollecting(null); setSel(new Set()); }}
        />
      )}
      {refunding && (
        <RefundModal target={refunding} custno={custno} onClose={() => setRefunding(null)} />
      )}
      {builder}

      {confirmDel && (
        <Modal
          open
          onClose={() => !del.isPending && setConfirmDel(null)}
          size="sm"
          title="Delete invoice?"
          footer={
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDel(null)}
                disabled={del.isPending}
                className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={del.isPending}
                onClick={() => {
                  const id = confirmDel.key.replace(/^peg-/, "");
                  del.mutate(id, { onSuccess: () => setConfirmDel(null) });
                }}
                className="rounded-md bg-red-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-red-700 disabled:bg-red-300"
              >
                {del.isPending ? "Deleting…" : "Delete invoice"}
              </button>
            </div>
          }
        >
          <div className="space-y-2 text-sm text-slate-600">
            <p>
              Permanently delete Pegasus invoice{" "}
              <span className="font-mono font-semibold text-slate-800">#{confirmDel.number}</span>
              {confirmDel.amount != null && (
                <> for <span className="font-semibold text-slate-800">{money(confirmDel.amount)}</span></>
              )}
              ? This removes the invoice, its line items, and the office labor it recorded.
            </p>
            {confirmDel.status === "issued" && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                This invoice is <strong>issued</strong>. Deleting it won&apos;t reopen the
                dispatch — reopen that separately if you need to re-bill.
              </p>
            )}
            {del.isError && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                {(del.error as Error)?.message ?? "Delete failed."}
              </p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// AR-360 STATEMENT — the "here's exactly what's open" document, grouped by
// property (ESC's statement ritual, one click). Opens a print-ready window;
// print it, save it as PDF, or read it to the caller. Numbers come straight
// from the same open-invoice math the tab shows — one source of truth.
// ---------------------------------------------------------------------------
function openStatementWindow({ customerName, custno, invoices, bal }: {
  customerName: string;
  custno: string;
  invoices: OpenInvoice[];
  bal?: { buckets: { current: number; d30: number; d60: number; d90: number }; total: number; mirrored: boolean };
}) {
  const esc = (v: string) => v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
  const byProp = new Map<string, OpenInvoice[]>();
  for (const i of invoices) {
    const k = i.property ?? "Property not recorded";
    byProp.set(k, [...(byProp.get(k) ?? []), i]);
  }
  const grand = invoices.reduce((sum, i) => sum + i.balance, 0);
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const groups = [...byProp.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([prop, rows]) => {
      const sub = rows.reduce((sum, r) => sum + r.balance, 0);
      const lines = rows
        .map((r) => `<tr><td>#${esc(r.number.replace(/^0+/, ""))}</td><td>${esc(r.date ?? "—")}</td><td>${esc(r.label)}</td><td class="n">${usd(r.total)}</td><td class="n b">${usd(r.balance)}</td></tr>`)
        .join("");
      return `<h2>${esc(prop)}</h2>
        <table><thead><tr><th>Invoice</th><th>Date</th><th>Type</th><th class="n">Amount</th><th class="n">Open</th></tr></thead>
        <tbody>${lines}</tbody>
        <tfoot><tr><td colspan="4">Subtotal — ${esc(prop)}</td><td class="n b">${usd(sub)}</td></tr></tfoot></table>`;
    })
    .join("");
  const aging = bal?.mirrored
    ? `<table class="aging"><thead><tr><th>Current</th><th>Over 30</th><th>Over 60</th><th>Over 90</th></tr></thead>
       <tbody><tr><td>${usd(bal.buckets.current)}</td><td>${usd(bal.buckets.d30)}</td><td>${usd(bal.buckets.d60)}</td><td class="hot">${usd(bal.buckets.d90)}</td></tr></tbody></table>`
    : "";
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Statement — ${esc(customerName)}</title>
  <style>
    body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;color:#1f2937;margin:40px auto;max-width:720px;padding:0 24px}
    header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #16314f;padding-bottom:14px;margin-bottom:20px}
    h1{font-size:22px;margin:0;color:#16314f} .meta{font-size:13px;color:#6b7280;text-align:right;line-height:1.5}
    h2{font-size:14px;margin:22px 0 6px;color:#334155}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#94a3b8;text-align:left;padding:4px 8px;border-bottom:1px solid #e2e8f0}
    td{padding:6px 8px;border-bottom:1px solid #f1f5f9} .n{text-align:right;font-variant-numeric:tabular-nums} .b{font-weight:700}
    tfoot td{border-top:1px solid #cbd5e1;border-bottom:0;font-weight:600;color:#334155}
    .total{margin-top:24px;display:flex;justify-content:flex-end;font-size:16px;font-weight:800;color:#16314f}
    .aging{margin-top:18px} .aging td{font-weight:600} .hot{color:#b91c1c}
    footer{margin-top:32px;border-top:1px solid #e2e8f0;padding-top:12px;font-size:12px;color:#6b7280;line-height:1.6}
    .print-btn{margin:18px 0;padding:9px 20px;background:#16314f;color:#fff;border:0;border-radius:8px;font-weight:700;cursor:pointer}
    @media print{.print-btn{display:none}body{margin:0}}
  </style></head><body>
  <header>
    <div><h1>Sedgwick Heating &amp; Air Conditioning</h1><div style="font-size:13px;color:#6b7280;margin-top:4px">Statement of Open Invoices</div></div>
    <div class="meta"><b>${esc(customerName)}</b><br/>Account ${esc(custno)}<br/>${today}</div>
  </header>
  <button class="print-btn" onclick="window.print()">Print / Save as PDF</button>
  ${groups}
  <div class="total">Total open&nbsp;&nbsp;${usd(grand)}</div>
  ${aging}
  <footer>Questions or paying by phone: (952) 881-9000 · contact@sedgwickheating.com<br/>
  Pay online any time from your customer portal at my.sedgwickheating.com.</footer>
  </body></html>`;
  const w = window.open("", "_blank", "noopener,width=820,height=900");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}
